import { useState } from 'react';
import type { WhatsappUsageCategory, WhatsappUsageNumber } from '@neuroedge/shared';
import { Card, PageState, Select } from '../../components/ui';
import { useWhatsappUsage } from '../../api/whatsapp-usage';

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;
const n = (v: number) => v.toLocaleString('en-IN');
const inr = (v: number) =>
  v.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });

const LABEL: Record<WhatsappUsageCategory, string> = {
  service: 'Replies and chat messages (service)',
  marketing: 'Marketing templates',
  utility: 'Utility templates',
  authentication: 'Authentication templates',
  free_entry: 'Free (from an ad or Facebook page)',
  other: 'Other',
};

/** The last 12 UTC months, newest first, as "YYYY-MM". */
function recentMonths(now = new Date()): string[] {
  return Array.from({ length: 12 }, (_, i) =>
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1)).toISOString().slice(0, 7),
  );
}
const monthLabel = (m: string) =>
  new Date(`${m}-01T00:00:00Z`).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

function FreeBar({ used, allowance }: { used: number; allowance: number }) {
  const pct = Math.min((used / allowance) * 100, 100);
  const tone = pct >= 100 ? 'var(--color-danger)' : pct >= 80 ? '#d97706' : 'var(--color-primary)';
  return (
    <div
      style={{ height: 6, borderRadius: 3, background: 'var(--color-border)', overflow: 'hidden' }}
      aria-hidden
    >
      <div style={{ height: '100%', width: `${pct}%`, background: tone }} />
    </div>
  );
}

function NumberUsage({ number }: { number: WhatsappUsageNumber }) {
  const s = number.service;
  return (
    <div
      role="group"
      aria-label={number.displayPhoneNumber ?? number.phoneNumberId}
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        borderTop: '1px solid var(--color-border)',
        paddingTop: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        <strong>{number.displayPhoneNumber ?? 'Removed number'}</strong>
        <span>{inr(number.estimatedCost)}</span>
      </div>
      <div style={{ display: 'grid', gap: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
          <span>Free service messages</span>
          <span
            style={s.delivered > s.freeAllowance ? { color: 'var(--color-danger)' } : undefined}
          >
            {n(s.freeUsed)} / {n(s.freeAllowance)}
          </span>
        </div>
        <FreeBar used={s.delivered} allowance={s.freeAllowance} />
        {!s.chargedAfterFree ? (
          <span style={muted}>Service messages were free this month.</span>
        ) : s.delivered > s.freeAllowance ? (
          <span style={muted}>
            {n(s.delivered - s.freeAllowance)} past the free {n(s.freeAllowance)} are charged.
          </span>
        ) : null}
      </div>
      {number.categories.length === 0 ? (
        <span style={muted}>Nothing delivered from this number this month.</span>
      ) : (
        <table
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
        >
          <thead>
            <tr style={{ ...muted, textAlign: 'left' }}>
              <th style={{ fontWeight: 500 }}>Kind</th>
              <th style={{ fontWeight: 500, textAlign: 'right' }}>Delivered</th>
              <th style={{ fontWeight: 500, textAlign: 'right' }}>Charged</th>
              <th style={{ fontWeight: 500, textAlign: 'right' }}>Estimate</th>
            </tr>
          </thead>
          <tbody>
            {number.categories.map((c) => (
              <tr key={c.category}>
                <td>
                  {LABEL[c.category]}
                  {c.rate !== null && c.charged > 0 ? (
                    <span style={muted}> · {inr(c.rate)} each</span>
                  ) : null}
                </td>
                <td style={{ textAlign: 'right' }}>{n(c.delivered)}</td>
                <td style={{ textAlign: 'right' }}>{n(c.charged)}</td>
                <td style={{ textAlign: 'right' }}>
                  {inr(c.estimatedCost)}
                  {c.notEstimated ? <span style={muted}> +{n(c.notEstimated)} abroad</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Billing page: WhatsApp messages delivered from each of the workspace's
 * numbers this month and roughly what Meta will bill for them (ADR-041).
 */
export function WhatsappUsageCard() {
  const months = recentMonths();
  const [month, setMonth] = useState(months[0]!);
  const usage = useWhatsappUsage(month);
  return (
    <Card title="WhatsApp usage">
      <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 720 }}>
        <div style={{ maxWidth: 220 }}>
          <Select
            label="Month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            options={months.map((m) => ({ value: m, label: monthLabel(m) }))}
          />
        </div>
        {usage.isPending ? (
          <PageState kind="loading" />
        ) : usage.isError ? (
          <PageState
            kind="error"
            message={usage.error.message}
            onRetry={() => void usage.refetch()}
          />
        ) : usage.data.numbers.length === 0 ? (
          <span style={muted}>No WhatsApp number connected yet.</span>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                gap: 'var(--space-4)',
                flexWrap: 'wrap',
                alignItems: 'baseline',
              }}
            >
              <span>
                <strong>{n(usage.data.totals.delivered)}</strong> delivered
              </span>
              <span>
                Estimated Meta bill <strong>{inr(usage.data.totals.estimatedTotal)}</strong>{' '}
                <span style={muted}>
                  ({inr(usage.data.totals.estimatedCost)} + {inr(usage.data.totals.estimatedGst)}{' '}
                  GST)
                </span>
              </span>
            </div>
            {usage.data.numbers.map((num) => (
              <NumberUsage key={num.phoneNumberId} number={num} />
            ))}
            <p style={{ margin: 0, ...muted }}>
              Meta bills you directly for WhatsApp; this is an estimate at Meta&apos;s rates for
              each recipient&apos;s country (before GST), counted from delivery receipts
              {usage.data.countingSince
                ? ` since ${new Date(usage.data.countingSince).toLocaleDateString('en-IN')}`
                : ''}
              . Months follow UTC, as Meta&apos;s do. Each number gets {n(1000)} free service
              messages a month; templates are charged unless Meta marks them free (e.g. utility
              inside an open chat). You get an alert at 800 and at 1,000 free messages. Countries
              without a rate are counted but not priced.
            </p>
          </>
        )}
      </div>
    </Card>
  );
}
